/**
 * useStartDiagnostic — kicks off the placement test from anywhere.
 *
 * Centralizes the "Start placement" action so the landing-page CTAs, the
 * /diagnostic intro page, and its mobile variant all share one code path:
 *
 *  1. Signed-out visitors get the placement-test auth gate (no API call). The
 *     signup flow then splits parent/child via its about + challenge steps.
 *  2. Signed-in visitors with a started-but-unfinished run RESUME it (→
 *     /diagnostic/play, which rehydrates via /v1/diagnostic/active) rather than
 *     starting a fresh run that would orphan the in-flight one.
 *  3. Otherwise signed-in visitors go to the /diagnostic/start chooser
 *     (TEX-1765), which asks parent/child: a parent sets up a "challenge your
 *     kid"; a student starts the placement solo.
 *
 * Returns `start`, `busy`, and `error` so callers can drive any button shape.
 * The actual /v1/diagnostic/start now happens in the chooser, so `busy`/`error`
 * stay quiet here (the navigation is instant).
 */

import * as React from "react";
import { useNavigate } from "react-router-dom";
import { useAuthGateOptional } from "../components/AuthGate.js";
import { useMe } from "./useMe.js";
import { usePlacementDone } from "./usePlacementDone.js";
import { clearDiagnosticTestSlug, stashDiagnosticTestSlug } from "../lib/diagnostic-test-slug.js";

interface UseStartDiagnostic {
  /** Begin the placement test (or the parent/child chooser when signed in). */
  start: () => Promise<void>;
  busy: boolean;
  error: string | null;
}

/**
 * @param testSlug Coverage-maps test slug when the start originates from a
 * test context (test landing page, `/diagnostic?test=sat`). Tailors the
 * placement to that test's profile server-side. Omit for generic starts.
 */
export function useStartDiagnostic(testSlug?: string): UseStartDiagnostic {
  const navigate = useNavigate();
  const { data: me } = useMe();
  // Read the gate optionally: this hook now drives always-mounted nav chrome
  // (mobile drawer + tab bar), which renders in isolated tests with no
  // provider. The gate is app-wide in production, so `gate` is non-null there.
  const gate = useAuthGateOptional();
  const { inProgress } = usePlacementDone();

  const start = React.useCallback(async (): Promise<void> => {
    if (!me?.user) {
      // The auth-gate round trip loses component state; the slug rides
      // sessionStorage so the gate's post-auth flow can forward it. A generic
      // start clears any stale stash (no inherited SAT profile).
      if (testSlug) stashDiagnosticTestSlug(testSlug);
      else clearDiagnosticTestSlug();
      if (gate) {
        gate.openAuthGate("placement-test", "/diagnostic");
      } else {
        // No provider (isolated tests only) — fall back to the intro page,
        // which carries its own Start button + gate.
        navigate("/diagnostic");
      }
      return;
    }
    // Started but didn't finish → resume the in-flight run instead of opening a
    // fresh one (which /start would do, abandoning their progress). The play
    // route rehydrates from /v1/diagnostic/active.
    if (inProgress) {
      navigate("/diagnostic/play");
      return;
    }
    // Signed in → ask parent/child first. The chooser starts the solo test or
    // sets up the challenge; the test slug rides router state.
    navigate("/diagnostic/start", { state: { testSlug: testSlug ?? null } });
  }, [me?.user, gate, inProgress, navigate, testSlug]);

  return { start, busy: false, error: null };
}
